import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Globe, Search, X, WifiOff, Check } from "lucide-react";
import { toast } from "sonner";
import { LANGUAGES, languageByCode } from "../lib/languages";
import { getState, subscribe, setLanguage, loadScript, builtinCode, isContractorPage } from "../lib/gtranslate";

export const LanguageSwitcher = ({ className = "" }) => {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(getState);
  const [q, setQ] = useState("");
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => { const off = subscribe(setState); const on = () => setOnline(navigator.onLine); window.addEventListener("online", on); window.addEventListener("offline", on); return () => { off(); window.removeEventListener("online", on); window.removeEventListener("offline", on); }; }, []);
  const contractor = isContractorPage();
  const builtin = (c) => contractor && !!builtinCode(c);
  const current = languageByCode(state.lang) || languageByCode("en");
  const hits = LANGUAGES.filter(([c, en, nat]) => !q || `${c} ${en} ${nat}`.toLowerCase().includes(q.toLowerCase()));
  const unavailable = !online || state.status === "unavailable";
  const pick = async (code) => {
    setOpen(false); setQ("");
    if (contractor && !builtin(code) && !online) {
      const fallback = builtinCode(state.lang) ? state.lang : "en";
      toast.message(`This language needs internet — showing ${fallback === "en" ? "English" : (languageByCode(fallback) || [])[2] || "English"}`);
      await setLanguage(fallback);
      return;
    }
    await setLanguage(code);
  };
  const openSheet = () => { setOpen(true); if (online && !(contractor && builtin(state.lang))) loadScript(); };

  return (
    <>
      <button type="button" className={`lang-switch notranslate ${className}`} translate="no" onClick={openSheet} aria-label="Change language" data-testid="language-switcher" data-lang={state.lang}>
        <Globe size={16} strokeWidth={2.5} /><span className="lang-code">{state.lang.split("-")[0].toUpperCase()}</span>
      </button>
      {open && createPortal(
        <div className="sheet-backdrop lang-sheet-backdrop" onClick={() => setOpen(false)} data-testid="language-sheet-backdrop">
          <div className="sheet lang-sheet notranslate" translate="no" onClick={(e) => e.stopPropagation()} data-testid="language-sheet">
            <div className="flex items-center justify-between">
              <p className="eyebrow">Language · {current[1]}</p>
              <button type="button" className="icon-btn" onClick={() => setOpen(false)} aria-label="Close" data-testid="language-sheet-close"><X size={16} /></button>
            </div>
            {unavailable && (
              <p className="text-xs mt-2 flex items-center gap-2 text-[var(--safflower)]" data-testid="language-unavailable"><WifiOff size={14} /> {contractor ? "Offline — languages marked “offline” still work." : "Translation needs internet — the app keeps working in English."}</p>
            )}
            <div className="search-row mt-3"><Search size={16} className="text-muted" /><input autoFocus className="flex-1 bg-transparent outline-none" placeholder="Search languages" value={q} onChange={(e) => setQ(e.target.value)} data-testid="language-search" /></div>
            <div className="mt-2 max-h-[55vh] overflow-y-auto" data-testid="language-list">
              <button type="button" className="country-item w-full text-left flex items-center justify-between" onClick={() => pick("en")} data-testid="language-option-en-original">
                <span><span className="font-semibold">English</span><span className="text-xs text-muted ml-2">original</span>{contractor && <OfflineDot />}</span>{state.lang === "en" && <Check size={16} className="text-primary" />}
              </button>
              {hits.filter(([c]) => c !== "en").map(([c, en, nat]) => (
                <button type="button" key={c} className="country-item w-full text-left flex items-center justify-between" onClick={() => pick(c)} disabled={unavailable && !contractor} data-testid={`language-option-${c}`}>
                  <span><span className="font-semibold">{nat}</span><span className="text-xs text-muted ml-2">{en}</span>{builtin(c) && <OfflineDot />}</span>{state.lang === c && <Check size={16} className="text-primary" />}
                </button>
              ))}
              {!hits.length && <p className="text-sm text-muted py-4 text-center">No matches.</p>}
            </div>
            <p className="text-[10px] text-muted mt-3">{contractor ? "Languages with the green dot are built in and work offline; others are translated by Google." : "Translated by Google. Job text you type is sent exactly as written."}</p>
          </div>
        </div>, document.body)}
    </>
  );
};

const OfflineDot = () => <span className="offline-dot" data-testid="language-offline-dot"><span className="dot" />offline</span>;
